import React from "react";
import ReusableModal from "../../../../components/Modals/ReusableModal";
import {
  useCreateProviderUserMutation,
  useUpdateUserEmailAndPhoneMutation,
} from "../../../../features/user/userApiSlice";
import { useCreateLogsMutation } from "../../../../features/logs/logsApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../features/auth/authSlice";
import {
  ACTIVITY_STATUS,
  ACTIVITY_TYPE,
} from "../../../../constants/config.constants";
import { useLSStore } from "../../../../context/ls-store";

const ProviderContactInfoModal = ({ open, setOpen, provider, setProvider }) => {
  const formatPhoneNumber = (input) => {
    if (!input) return "";

    const cleaned = ("" + input).replace(/\D/g, "");

    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    return match
      ? [match[1], match[2], match[3]].filter(Boolean).join("-")
      : "";
  };

  const [providerEmail, setProviderEmail] = React.useState(
    provider.email === "NA" ? "" : provider.email
  );
  const [providerPhone, setProviderPhone] = React.useState(
    provider.phone === "NA" ? null : formatPhoneNumber(provider.phone)
  );
  const [errorMsg, setErrorMsg] = React.useState("");

  const setAlert = useLSStore((state) => state.setAlert);

  const [createProviderUser, { isLoading: userLoading }] =
    useCreateProviderUserMutation();

  const [updateUserApi, { isLoading: updateLoading }] =
    useUpdateUserEmailAndPhoneMutation();

  const [createLog] = useCreateLogsMutation();

  const user = useSelector(selectUser);

  const inviteProvider = async (data) => {
    try {
      const payload = {
        activity: `User ${user?.details?.name} has invited provider ${provider.name} to join CareSMS`,
        type: ACTIVITY_TYPE.PROVIDER_UPDATE,
        data: {
          providerId: provider.id,
        },
      };

      try {
        await createProviderUser({
          providerId: provider.id,
          userData: {
            type: 3,
            email: providerEmail,
            phone: providerPhone || null,
          },
        }).unwrap();

        await createLog({
          ...payload,
          status: ACTIVITY_STATUS.SUCCESS,
        }).unwrap();

        setAlert({
          message: "Provider updated Successfully",
          severity: "success",
        });
      } catch (error) {
        await createLog({
          ...payload,
          status: ACTIVITY_STATUS.FAILED,
        }).unwrap();

        throw new Error(error.data?.message || error.message);
      }
    } catch (error) {
      throw new Error(error.message);
    }
  };

  const updateContactDetails = async (data) => {
    try {
      const payload = {
        activity: `User ${user?.details?.name} has updated the data for provider ${provider.name}.`,
        type: ACTIVITY_TYPE.PROVIDER_UPDATE,
        data: {
          providerId: provider.id,
        },
      };

      try {
        await updateUserApi({
          id: provider.id,
          type: 3,
          phone: providerPhone || null,
          email: providerEmail,
        }).unwrap();

        await createLog({
          ...payload,
          status: ACTIVITY_STATUS.SUCCESS,
        }).unwrap();

        setAlert({
          message: "Contact Details Updated Successfully",
          severity: "success",
        });
      } catch (error) {
        await createLog({
          ...payload,
          status: ACTIVITY_STATUS.FAILED,
        }).unwrap();

        throw new Error(error.data?.message || error.message);
      }
    } catch (error) {
      throw new Error(error.message);
    }
  };

  const handleSubmit = async () => {
    if (providerEmail === "") {
      setErrorMsg("Email is required");
      return;
    }

    try {
      if (provider.email === "NA") {
        // await inviteProvider();
        await updateContactDetails();
      } else {
        await updateContactDetails();
      }

      setProvider((prev) => ({
        ...prev,
        email: providerEmail,
        phone: providerPhone,
      }));

      setErrorMsg("");
      setProviderEmail("");
      setProviderPhone(null);
      setOpen(false);
    } catch (error) {
      setErrorMsg("Failed to update contact information");
    }
  };

  return (
    <ReusableModal
      open={open}
      title="Update Contact Information"
      onClose={() => {
        setOpen(false);
      }}
    >
      <div className="flex flex-col w-full gap-4 p-6 min-w-[500px]">
        <div className="flex flex-col gap-2 w-full">
          <label htmlFor="email" className="m-0 text-base font-semibold">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Enter Email"
            className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
            onChange={(e) => setProviderEmail(e.target.value)}
            value={providerEmail}
          />
        </div>
        <div className="flex flex-col gap-2 w-full">
          <label htmlFor="phone" className="m-0 text-base font-semibold">
            Phone (optional)
          </label>
          <input
            type="tel"
            id="tel"
            name="tel"
            className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
            placeholder="xxx-xxx-xxxx"
            value={providerPhone ?? ""}
            onChange={(e) => {
              if (e.target.value.length >= 13) {
                return;
              }
              const formatted = formatPhoneNumber(e.target.value);
              setProviderPhone(formatted || null);
            }}
          />
        </div>
        {errorMsg && <div className="text-red-500">{errorMsg}</div>}
        <div className="flex flex-row gap-6 justify-between items-center w-full">
          <button
            className="w-1/2 p-2 font-semibold rounded-md  border-[2px] text-primaryblue border-primaryblue"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            disabled={providerEmail === "" || userLoading || updateLoading}
            className="w-1/2 disabled:opacity-40 disabled:cursor-not-allowed font-semibold p-2 border-[2px] rounded-md bg-primaryblue border-primaryblue text-white whitespace-nowrap"
            onClick={async () => {
              await handleSubmit();
              setOpen(false);
            }}
          >
            {/* {provider.email === "NA"
              ? `Invite ${provider?.name?.split(" ")[0]}`
              : "Save Changes"} */}
              Save Changes
          </button>
        </div>
      </div>
    </ReusableModal>
  );
};

export default ProviderContactInfoModal;